import { createFileRoute } from '@tanstack/react-router'
import { Alert, AlertDescription, AlertTitle } from '@nxt-shared/ui/components/Alert'
import { Badge } from '@nxt-shared/ui/components/Badge'
import { Button } from '@nxt-shared/ui/components/Button'
import { Card } from '@nxt-shared/ui/components/Card'
import { Skeleton } from '@nxt-shared/ui/components/Skeleton'
import {
  useMemberOrganizationGroups,
  liveblogOrganizationUrl,
  nxtCmsOrganizationUrl,
  managementTenantUrl,
  type LiveblogOrganization,
  type NxtOrganization,
  type MeOrganizationRole,
  type TenantGroup,
} from '@nxt-platform/global-components'
import { usePageTitle } from '../../features/page-title/pageTitle'

export const Route = createFileRoute('/me/')({
  component: TenantListRoute,
})

const roleBadgeVariant: Record<MeOrganizationRole, 'default' | 'secondary' | 'outline'> = {
  owner: 'default',
  admin: 'secondary',
  user: 'outline',
  read: 'outline',
}

function TenantListRoute() {
  usePageTitle('Organizations')
  // Liveblog (gatekeeper) and nxt-cms (management) load independently through the shared hook —
  // neither blocks the other, and each keeps its own loading/error/retry state below.
  const { groups, liveblog, nxt } = useMemberOrganizationGroups()

  const liveblogLoading = liveblog.status === 'loading'
  const nxtLoading = nxt.status === 'loading'

  const bothLoading = liveblogLoading && nxtLoading
  const settled = !liveblogLoading && !nxtLoading
  const isEmpty = settled && groups.length === 0 && liveblog.status !== 'error' && nxt.status !== 'error'

  return (
    <div className="mx-auto w-full max-w-3xl">
      <h1 className="mb-1 text-2xl font-bold">Your organizations</h1>
      <p className="mb-6 text-sm text-muted-foreground">
        Open a liveblog or nxt-cms organization you have access to.
      </p>

      {bothLoading && <OrganizationsSkeleton />}

      {/* Independent error affordances: one product failing never hides the other. */}
      {liveblog.status === 'error' && (
        <Alert variant="danger" className="mb-6">
          <AlertTitle>Couldn’t load your liveblog organizations</AlertTitle>
          <AlertDescription>
            <span>{liveblog.error?.message ?? 'Something went wrong.'}</span>
            <Button variant="outline-muted" size="sm" icon={false} className="mt-2" onClick={liveblog.refetch}>
              Try again
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {nxt.status === 'error' && (
        <Alert variant="danger" className="mb-6">
          <AlertTitle>Couldn’t load your nxt-cms organizations</AlertTitle>
          <AlertDescription>
            <span>{nxt.error?.message ?? 'Something went wrong.'}</span>
            <Button variant="outline-muted" size="sm" icon={false} className="mt-2" onClick={nxt.refetch}>
              Try again
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {!bothLoading && groups.length > 0 && (
        <div className="flex flex-col gap-6">
          {groups.map((group) => (
            <TenantSection key={group.id} group={group} />
          ))}
        </div>
      )}

      {isEmpty && <EmptyState />}
    </div>
  )
}

function TenantSection({ group }: { group: TenantGroup }) {
  return (
    <section>
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
          {group.name}
        </h2>
        {group.canManage && (
          <a
            href={managementTenantUrl(group.id)}
            className="shrink-0 text-xs font-medium text-primary hover:underline"
          >
            Manage tenant
          </a>
        )}
      </div>
      <Card size="sm" className="gap-0 divide-y divide-border py-0">
        {group.liveblogOrgs.map((org) => (
          <LiveblogOrganizationRow key={`liveblog-${org.id}`} org={org} />
        ))}
        {group.nxtOrgs.map((org) => (
          <NxtOrganizationRow key={`nxt-${org.id}`} org={org} />
        ))}
      </Card>
    </section>
  )
}

function LiveblogOrganizationRow({ org }: { org: LiveblogOrganization }) {
  return (
    <a
      href={liveblogOrganizationUrl(org)}
      className="flex items-center justify-between gap-3 px-4 py-3 transition-colors first:rounded-t-lg last:rounded-b-lg hover:bg-muted"
    >
      <div className="min-w-0">
        <div className="truncate font-bold">{org.name}</div>
        {org.shortName && (
          <div className="truncate text-sm text-muted-foreground">{org.shortName}</div>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Badge variant="info">Liveblog</Badge>
        <Badge variant={roleBadgeVariant[org.role]}>{org.role}</Badge>
      </div>
    </a>
  )
}

function NxtOrganizationRow({ org }: { org: NxtOrganization }) {
  return (
    <a
      href={nxtCmsOrganizationUrl(org)}
      className="flex items-center justify-between gap-3 px-4 py-3 transition-colors first:rounded-t-lg last:rounded-b-lg hover:bg-muted"
    >
      <div className="min-w-0">
        <div className="truncate font-bold">{org.name}</div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Badge variant="default">NXT</Badge>
        <Badge variant={org.accessLevel === 'admin' ? 'secondary' : 'outline'}>{org.accessLevel}</Badge>
      </div>
    </a>
  )
}

function EmptyState() {
  return (
    <Card size="sm" className="items-center px-6 text-center">
      <p className="font-bold">No organizations yet</p>
      <p className="text-sm text-muted-foreground">
        You don’t have access to any liveblog or nxt-cms organizations.
      </p>
    </Card>
  )
}

function OrganizationsSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      {[0, 1].map((section) => (
        <section key={section}>
          <Skeleton className="mb-2 h-3 w-32" />
          <Card size="sm" className="gap-0 divide-y divide-border py-0">
            {[0, 1].map((row) => (
              <div key={row} className="flex items-center justify-between gap-3 px-4 py-3">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-5 w-14" />
              </div>
            ))}
          </Card>
        </section>
      ))}
    </div>
  )
}
